import React, { useEffect, useRef, useState } from 'react';
import { Button, Grid, makeStyles, Typography } from '@quillbot/design-system';
import SolidBorderInput from '@Common/quillElements/input/SolidBorder';
import Link from 'next/link';
import ClearButton from '@Common/quillElements/button/ClearButton';
import { validEmail } from 'core/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
// import { RootState } from 'src/_common/models/redux/store';
import { RootState } from 'src/_common/models/redux/store';
import { handleAuthTrack, login } from '../handlers/authentication';
import { loadingAction } from '../actions/authenticationActions';
import Img from '@Common/components/img';

const useStyles = makeStyles((theme) => ({
  socialBtn: {
    // backgroundColor: '#f1f2fa',
    backgroundColor: theme.palette.background.default,
    height: '45px',
    width: '100%',
    textTransform: 'inherit',
    padding: '12px',
    '&:hover': {
      // backgroundColor: '#f6f6f9',
    },
  },
  box: {
    // background: '#F4F4F4',
    borderRadius: '4px',
    padding: '6px 20px 6px 17px',
  },
  boxText: {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontsize: '12px',
    lineHeight: '16px',
  },
  socialBtnText: {},
  inputTextShrink: {
    // transform: 'translate(14px 0px) scale(0.75)',
  },
  btnText: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: 15,
    },
  },
  header: {
    fontSize: '28px',
    textAlign: 'center',
    paddingTop: '25px',
  },
  subHeader: {
    fontSize: '17px',
    textAlign: 'center',
  },
  coreContainer: {
    maxWidth: '336px',
    padding: '24px 0px 60px',
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 15px 60px',
      width: '100%',
    },
  },
  loginHolder: {
    [theme.breakpoints.down('sm')]: {
      padding: '15px',
    },
    backgroundColor: theme.palette.grey[50],
    '& .auth-container': {
      boxShadow: theme.shadows[3],
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

function Login(props: { onAuthSuccess: Function; minimalMode?: boolean }) {
  const emailField = useRef<HTMLInputElement>(null);
  const passwordField = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState('');
  const [emailValidationError, setEmailValidationError] = useState('');
  const [errorFields, setErrorFields] = useState<string[]>([]);
  // const [success, setSuccess] = useState(false)
  // const [containerStyles, setContainerStyles] = useState({});
  const dispatch = useDispatch();
  const extensionMetaData = useSelector(
    (state: RootState) => state.common.platform.extensionMetaData
  );

  useEffect(() => {
    handleAuthTrack(router);
  }, []);

  const handleLogin = (method: 'google' | 'facebook' | 'email') => {
    const email = emailField?.current?.value;
    const password = passwordField?.current?.value;
    if (method === 'email') {
      if (!validEmail(`${email}`)) {
        setEmailValidationError('Please enter valid email');
        return;
      }
      if (!password || password.length < 6) {
        setErrorFields(['password']);
        setErrorMessage('Minimum 6 characters');
        return;
      }
      if (password.length > 72) {
        setErrorFields(['password']);
        setErrorMessage('Maxmimum 72 characters');
        return;
      }
    }
    dispatch(loadingAction(true));
    login(method, `${email}`, `${password}`)
      .then((res: unknown) => {
        props.onAuthSuccess(res);
      })
      .catch((error: { errorMessage: string; errorFields?: string[] }) => {
        if (error && error.errorMessage) {
          setErrorMessage(error.errorMessage);
          if (error.errorFields) {
            setErrorFields(error.errorFields);
          }
        }
        // else setSuccess(false)
        dispatch(loadingAction(false));
      });
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleLogin('email');
    }
  };

  const resetState = () => {
    setEmailValidationError('');
    setErrorMessage('');
    setErrorFields([]);
    // setSuccess(false)
  };

  const resetError = (field: string) => () => {
    if (field === 'username') {
      if (errorFields.length && errorFields.includes('email')) resetState();
      else if (emailValidationError) setEmailValidationError('');
    } else if (field === 'password') {
      if (errorFields.length && errorFields.includes('password')) resetState();
    }
  };

  const isWordExt = extensionMetaData?.type === 'ms-word';
  return (
    <Grid container className={!props.minimalMode ? classes.loginHolder : ''}>
      {!props.minimalMode ? (
        <>
          <Grid item xs={12}>
            <Typography component="h1" classes={{ body1: classes.header }}>
              {isWordExt
                ? 'Welcome to QuillBot'
                : 'Log in to your QuillBot account'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography classes={{ body1: classes.subHeader }}>
              {isWordExt
                ? 'Trusted by millions worldwide.'
                : // : `Paraphrase ${CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_MEMBER} characters with a free account.`}
                  null}
            </Typography>
          </Grid>
        </>
      ) : (
        <></>
      )}
      <div
        className={!props.minimalMode ? 'auth-container' : ''}
        id="loginContainer"
      >
        {/* <div className={!props.minimalMode ? '"auth-container-top-border' : ''} /> */}
        <Grid container className={classes.coreContainer} spacing={1}>
          {isWordExt ? (
            <>
              <Grid item xs={12} container>
                <Grid item className={`${classes.box} ${classes.boxText}`}>
                  <p>Google & Facebook log in are currently unavailable</p>
                  <p style={{ fontWeight: 'normal' }}>
                    Please{' '}
                    <a
                      className="btn-link"
                      target="_blank"
                      href="/login?returnUrl=/settings&action=addPwd&source=word"
                    >
                      {' '}
                      add a password
                    </a>{' '}
                    to log in.
                  </p>
                </Grid>
              </Grid>
              <Grid item xs style={{ marginBottom: 0, textAlign: 'center' }}>
                <p
                  className={classes.boxText}
                  style={{ marginBottom: 0, fontWeight: 600 }}
                >
                  Log in with Email and Password
                </p>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} container>
                <Button
                  classes={{
                    root: classes.socialBtn,
                    text: classes.socialBtnText,
                  }}
                  onClick={() => handleLogin('google')}
                  className="social-auth-btn"
                  color="inherit"
                >
                  <Img src="/emojis/solid_google.svg" alt="google logo" />
                  <span className={classes.btnText}>Continue with Google</span>
                </Button>
              </Grid>
              <Grid item xs={12} container>
                <Button
                  onClick={() => handleLogin('facebook')}
                  classes={{
                    root: classes.socialBtn,
                    text: classes.socialBtnText,
                  }}
                  className="social-auth-btn"
                  color="inherit"
                >
                  <Img
                    src="/emojis/solid_facebook.svg"
                    style={{ left: '18.5px' }}
                    alt="facebook logo"
                  />
                  <span className={classes.btnText}>
                    Continue with Facebook
                  </span>
                </Button>
              </Grid>
              <Grid item xs={12} container alignItems="center">
                <Typography
                  align="center"
                  variant="caption"
                  style={{ textAlign: 'center', width: '100%' }}
                >
                  OR
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <SolidBorderInput
              fullWidth
              size="small"
              label="Email"
              defaultValue=""
              inputRef={emailField}
              variant="filled"
              sensitive
              error={Boolean(
                emailValidationError ||
                  (errorMessage && errorFields.includes('email'))
              )}
              onKeyUp={handleKeyUp}
              helperText={emailValidationError}
              onChange={resetError('username')}
            />
          </Grid>
          <Grid item xs={12}>
            <SolidBorderInput
              error={Boolean(errorMessage) && errorFields.includes('password')}
              size="small"
              fullWidth
              type="password"
              label="Password"
              inputRef={passwordField}
              defaultValue=""
              variant="filled"
              sensitive
              onKeyUp={handleKeyUp}
              onChange={resetError('password')}
            />
          </Grid>
          {errorMessage && (
            <Grid item xs={12}>
              <Typography variant="caption" color="error">
                {errorMessage}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} style={{ padding: '12px 4px' }}>
            <Button
              variant="contained"
              color="primary"
              className="auth-btn"
              fullWidth
              onClick={() => handleLogin('email')}
            >
              Log In
            </Button>
          </Grid>
          {!props.minimalMode && (
            <Grid item xs={12} className="qbp-textCenter">
              <Link href={{ pathname: '/signup', query: { ...router.query } }}>
                <ClearButton className="btn-link" type="button">
                  Don’t have an account?
                </ClearButton>
              </Link>
            </Grid>
          )}

          {isWordExt && (
            <Grid item xs={12} className="qbp-textCenter">
              <p style={{ margin: 0, padding: 0 }}>
                Need Help?{' '}
                <a
                  href="https://help.quillbot.com/hc/en-us/articles/360056805014-I-can-t-log-in-with-Google-or-Facebook-What-should-I-do-"
                  target="_blank"
                  className="btn-link"
                  rel="noreferrer"
                >
                  Visit Help Center
                </a>
              </p>
            </Grid>
          )}
          {!props.minimalMode && (
            <Grid item xs={12} className="qbp-textCenter">
              <Link
                href={{ pathname: '/recovery', query: { ...router.query } }}
              >
                <ClearButton className="btn-link" type="button">
                  Reset Password
                </ClearButton>
              </Link>
            </Grid>
          )}
        </Grid>
      </div>
    </Grid>
  );
}

export default Login;
